/**
 * Utility service to dispatch application lead notifications and payment success invoices
 * to Admin (admin@formsadda.com) and applicant user email formatted as an HTML Table.
 */

export const ADMIN_EMAIL = 'admin@formsadda.com';
export const ADMIN_PASSWORD = 'FormsADDA@1994';

/**
 * Helper to build an HTML Table string for email renderers
 */
function generateHtmlTable(title, rows) {
  const rowHtml = rows.map(([key, val]) => `
    <tr>
      <td style="padding: 10px 14px; border: 1px solid #cbd5e1; background-color: #f8fafc; font-weight: bold; color: #0f2537; width: 38%; font-size: 14px;">${key}</td>
      <td style="padding: 10px 14px; border: 1px solid #cbd5e1; color: #1e293b; font-size: 14px;">${val}</td>
    </tr>
  `).join('');

  return `
    <div style="font-family: Arial, sans-serif; max-width: 650px; margin: 0 auto; padding: 15px; border: 1px solid #e2e8f0; border-radius: 10px; background-color: #ffffff;">
      <h2 style="background-color: #0f2537; color: #ffffff; padding: 14px 18px; margin: -15px -15px 15px -15px; border-radius: 10px 10px 0 0; font-size: 18px;">
        ${title}
      </h2>
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
        <tbody>
          ${rowHtml}
        </tbody>
      </table>
      <div style="text-align: center; font-size: 12px; color: #64748b; margin-top: 10px; padding-top: 10px; border-top: 1px dashed #cbd5e1;">
        Official Notification generated by <strong>FormsADDA Admin Engine</strong> (admin@formsadda.com)
      </div>
    </div>
  `;
}

/**
 * Dispatch initial leadform data to Admin mail in HTML Table format
 */
export async function sendApplicationEmails(leadData) {
  const adminEmail = ADMIN_EMAIL;
  const userEmail = leadData.email || leadData.userEmail || '';
  const fullName = leadData.fullName || leadData.name || 'Applicant';
  const phone = leadData.phone || leadData.mobile || '';
  const course = leadData.course || leadData.university || 'Online MBA';
  const amount = leadData.amount || '₹500';
  const timestamp = new Date().toLocaleString();

  const tableRows = [
    ['Applicant Name', fullName],
    ['Mobile Number', `+91 ${phone}`],
    ['Email Address', userEmail],
    ['Selected Course', course],
    ['Registration Token Fee', amount],
    ['Submission Date & Time', timestamp],
    ['Admin Recipient', adminEmail],
    ['Admin Password', ADMIN_PASSWORD]
  ];

  const htmlContent = generateHtmlTable('NEW ADMISSION LEAD RECEIVED', tableRows);

  const payload = {
    _subject: `[New Lead] ${fullName} - ${course}`,
    _template: 'table',
    _captcha: 'false',
    'Applicant Name': fullName,
    'Mobile Number': `+91 ${phone}`,
    'Email Address': userEmail,
    'Selected Course': course,
    'Registration Token Fee': amount,
    'Submission Date & Time': timestamp,
    'Admin Email': adminEmail,
    'Admin Portal Password': ADMIN_PASSWORD,
    html_body: htmlContent
  };

  console.log(`[Email Dispatch] Sending lead details table to Admin (${adminEmail}) and User (${userEmail}):`, payload);

  try {
    const params = new URLSearchParams({
      admin_email: adminEmail,
      user_email: userEmail,
      name: fullName,
      phone: phone,
      course: course,
      amount: amount,
      html_table: htmlContent
    });
    
    // Beacon API dispatch to FormsADDA Backend Mailer
    const img = new Image();
    img.src = `https://formsadda.com/api/lead_email.php?${params.toString()}`;
    
    // FormSubmit AJAX HTML Table dispatch directly to admin@formsadda.com
    fetch(`https://formsubmit.co/ajax/${adminEmail}`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    }).catch((err) => console.log('Mail API response:', err));
  } catch (err) {
    console.log('[Email Dispatch] Exception:', err);
  }

  return { success: true, adminEmail, userEmail };
}

/**
 * Dispatch payment success email with generated Application Number & Invoice data
 * formatted as an HTML Table to User Email and Admin Email (admin@formsadda.com)
 */
export async function sendPaymentSuccessEmail(paymentData) {
  const adminEmail = ADMIN_EMAIL;
  const userEmail = paymentData.email || 'applicant@formsadda.com';
  const fullName = paymentData.name || 'Applicant';
  const phone = paymentData.phone || paymentData.mobile || '';
  const course = paymentData.course || paymentData.university || 'Online MBA';
  const amount = paymentData.amount || '500';
  const applicationNo = paymentData.applicationNo || 'Manipal-University-27001';
  const txnId = paymentData.txnId || 'PAYU_TXN';
  const timestamp = paymentData.date || new Date().toLocaleString();

  const tableRows = [
    ['Application Number', applicationNo],
    ['PayU Transaction ID', txnId],
    ['Payment Status', 'SUCCESS (CONFIRMED & VERIFIED)'],
    ['Applicant Name', fullName],
    ['Mobile Number', `+91 ${phone}`],
    ['Email Address', userEmail],
    ['Selected Course', course],
    ['Amount Paid', `₹${amount}`],
    ['Payment Gateway', 'PayU Live Gateway (Merchant Key: 3bqRWp)'],
    ['Date & Time', timestamp],
    ['Admin Recipient', adminEmail],
    ['Admin Portal Password', ADMIN_PASSWORD]
  ];

  const htmlContent = generateHtmlTable(`OFFICIAL PAYMENT & ADMISSION INVOICE (${applicationNo})`, tableRows);

  const payload = {
    _subject: `[Payment CONFIRMED - ${applicationNo}] ${fullName} - ${course}`,
    _template: 'table',
    _captcha: 'false',
    'Application Number': applicationNo,
    'PayU Transaction ID': txnId,
    'Payment Status': 'SUCCESS (CONFIRMED & VERIFIED)',
    'Applicant Name': fullName,
    'Mobile Number': `+91 ${phone}`,
    'Email Address': userEmail,
    'Selected Course': course,
    'Amount Paid': `₹${amount}`,
    'Payment Gateway': 'PayU Live Gateway (Merchant Key: 3bqRWp)',
    'Date & Time': timestamp,
    'Admin Recipient': adminEmail,
    'Admin Portal Password': ADMIN_PASSWORD,
    html_body: htmlContent
  };

  console.log(`[Invoice Email] Sending Invoice Table (${applicationNo}) to User (${userEmail}) & Admin (${adminEmail}):`, payload);

  try {
    const params = new URLSearchParams({
      admin_email: adminEmail,
      user_email: userEmail,
      application_no: applicationNo,
      txnid: txnId,
      name: fullName,
      phone: phone,
      course: course,
      amount: amount,
      status: 'SUCCESS',
      html_table: htmlContent
    });

    // Beacon API dispatch to FormsADDA Invoice Mailer
    const img = new Image();
    img.src = `https://formsadda.com/api/payment_invoice.php?${params.toString()}`;

    // FormSubmit AJAX HTML Table dispatch directly to admin@formsadda.com
    fetch(`https://formsubmit.co/ajax/${adminEmail}`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    }).catch((err) => console.log('Invoice Mail response:', err));
  } catch (err) {
    console.log('[Invoice Email] Exception:', err);
  }

  return { success: true, applicationNo, adminEmail, userEmail };
}
